import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Alert, Spinner, ListGroup } from 'react-bootstrap';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const RoleManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Role permissions configuration
  const rolePermissions = {
    admin: [
      'View all streams',
      'Create/edit/delete any stream',
      'Manage users',
      'Manage roles',
      'Access settings',
      'View analytics',
      'Manage system configuration'
    ],
    user: [
      'View own streams only',
      'Create own streams',
      'Edit own streams',
      'Delete own streams',
      'View own analytics',
      'Manage own profile'
    ]
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('streamToken');
      const response = await axios.get(`${API_URL}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.data);
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('streamToken');
      await axios.put(`${API_URL}/auth/users/${userId}`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
      setShowModal(false);
    } catch (err) {
      setError('Failed to update role');
    }
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  if (loading) return <Spinner animation="border" />;

  return (
    <div className="container-fluid">
      <h2 className="mb-4">Role Management</h2>
      
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      
      <div className="row">
        <div className="col-md-8">
          <Card>
            <Card.Header>
              <h5 className="mb-0">User Roles</h5>
            </Card.Header>
            <Card.Body>
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Current Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>
                        <Badge bg={user.role === 'admin' ? 'danger' : 'primary'}>
                          {user.role}
                        </Badge>
                      </td>
                      <td>
                        <Button variant="info" size="sm" onClick={() => openRoleModal(user)}>
                          Change Role
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </div>

        <div className="col-md-4">
          <Card className="mb-3">
            <Card.Header>
              <h5 className="mb-0">Admin Permissions</h5>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                {rolePermissions.admin.map((perm, idx) => (
                  <ListGroup.Item key={idx} className="d-flex align-items-center">
                  <i className="fas fa-check text-success me-2"></i>
                    {perm}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <h5 className="mb-0">User Permissions</h5>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                {rolePermissions.user.map((perm, idx) => (
                  <ListGroup.Item key={idx} className="d-flex align-items-center">
                  <i className="fas fa-check text-primary me-2"></i>
                    {perm}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Change User Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p>Change role for <strong>{selectedUser.username}</strong></p>
              <Form>
                <Form.Group>
                  <Form.Label>Select New Role</Form.Label>
                  <Form.Select 
                    value={selectedUser.role}
                    onChange={(e) => handleRoleChange(selectedUser.id, e.target.value)}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                </Form.Group>
              </Form>
              <div className="mt-3">
                <h6>Role Permissions:</h6>
                <ListGroup variant="flush">
                  {(selectedUser.role === 'admin' ? rolePermissions.admin : rolePermissions.user).map((perm, idx) => (
                    <ListGroup.Item key={idx} className="py-1">
                      <small>• {perm}</small>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RoleManagement;
